import * as React from 'react';
import { useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Menu, Avatar, Icon, icons, useDialog } from '@agentisai/design-system';
import { useAuth } from '../auth/AuthProvider';
import { useViewportTier } from '../hooks/useViewportTier';
import {
  trigger as triggerCls,
  backdrop as backdropCls,
  sheetWrap as sheetWrapCls,
  sheet as sheetCls,
  dragHandle as dragHandleCls,
  sheetHeader as sheetHeaderCls,
  sheetName as sheetNameCls,
  sheetMeta as sheetMetaCls,
  sheetSection as sheetSectionCls,
  sheetItem as sheetItemCls,
  sheetItemIcon as sheetItemIconCls,
} from './UserMenu.css';

/**
 * UserMenu (S-H2) — the top-right avatar that opens a profile / account
 * menu with a working Sign Out, rendering the REAL signed-in claims.
 *
 * Lives in `apps/ui/web` (not the DS) because it couples to `useAuth()` — the
 * Auth0 client boundary (frontend-architecture R0 + R13). It is pure
 * COMPOSITION of DS primitives: the regular-tier popover is the DS `Menu`
 * (with its `header` slot + `section` item + `Menu.Header`, shipped in
 * S-H1); the compact-tier (<768px) bottom-sheet is a `useDialog`-backed
 * surface mirroring the DS `Modal` bottom-sheet shape (decision 7a —
 * app-local, not promoted to the DS).
 *
 * The two presentations share the SAME identity (name + email) and the
 * SAME two actions (Profile — a Drop-1 decorative no-op; Sign Out — calls
 * `logout()`), derived once in render (no `useMemo` — the React Compiler
 * memoizes; R2/R11).
 */
export function UserMenu() {
  const { user, tenantBrand, logout } = useAuth();
  const tier = useViewportTier();

  // Real claims (never the prototype's "Jaco Vorster / JV"). The Avatar
  // derives initials from `fullName`; the email line is hidden when absent.
  const fullName = user?.name ?? 'User';
  const email = user?.email ?? '';
  const tint = tenantBrand?.avatarTint;

  // The avatar is a non-focusable <span role="img"> — wrap it in a real
  // <button> so the trigger is keyboard-operable and carries the accessible
  // name + aria-haspopup/expanded (the DS Menu/Popover wire those on it).
  const avatarTrigger = (
    <button type="button" className={triggerCls} aria-label={`Account menu for ${fullName}`}>
      <Avatar name={fullName} size="sm" style={tint ? { background: tint } : undefined} />
    </button>
  );

  if (tier === 'compact') {
    return (
      <CompactUserMenu
        trigger={avatarTrigger}
        fullName={fullName}
        email={email}
        onSignOut={logout}
      />
    );
  }

  return (
    <Menu
      aria-label="Account"
      placement="bottom-end"
      minWidth={240}
      trigger={avatarTrigger}
      header={<Menu.Header name={fullName} email={email} />}
      items={[
        { kind: 'section', id: 'account', label: 'Account' },
        {
          id: 'profile',
          label: 'Profile',
          icon: <Icon as={icons.User} size={16} />,
          // Drop-1 decorative no-op — there is no profile surface yet.
          onSelect: () => {},
        },
        {
          id: 'signout',
          label: 'Sign Out',
          icon: <Icon as={icons.CornerDownLeft} size={16} />,
          onSelect: () => logout(),
        },
      ]}
    />
  );
}
UserMenu.displayName = 'UserMenu';

interface CompactUserMenuProps {
  trigger: React.ReactElement<{ onClick?: React.MouseEventHandler<HTMLElement> }>;
  fullName: string;
  email: string;
  onSignOut: () => void;
}

/**
 * Compact bottom-sheet (<768px). A `role="dialog"` surface delegating focus
 * trap + Escape + scroll-lock + focus-restore to `useDialog` (the DS hook —
 * frontend-architecture R7), sharing the same identity + actions as the
 * desktop popover. Slides up honouring `prefers-reduced-motion`; ≥44px rows.
 */
function CompactUserMenu({ trigger, fullName, email, onSignOut }: CompactUserMenuProps) {
  const [open, setOpen] = useState(false);
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const dialogTitleId = useId();

  const close = () => setOpen(false);

  useDialog({
    open,
    containerRef: sheetRef,
    onClose: close,
    // Pointer-opened sheet may leave focus on <body>; restore to the avatar.
    returnFocusRef: triggerRef,
  });

  // Clone the shared avatar trigger to own its ref + click-to-open, so the
  // trigger element is identical across both tiers.
  const triggerNode = React.cloneElement(
    trigger as React.ReactElement<{
      ref?: React.Ref<HTMLButtonElement>;
      onClick?: React.MouseEventHandler<HTMLElement>;
      'aria-haspopup'?: React.AriaAttributes['aria-haspopup'];
      'aria-expanded'?: boolean;
    }>,
    {
      ref: triggerRef,
      onClick: () => setOpen(true),
      'aria-haspopup': 'dialog',
      'aria-expanded': open,
    },
  );

  const runAndClose = (fn: () => void) => () => {
    fn();
    close();
  };

  return (
    <>
      {triggerNode}
      {open &&
        typeof document !== 'undefined' &&
        createPortal(
          <>
            <div className={backdropCls} onClick={close} aria-hidden="true" />
            <div className={sheetWrapCls}>
              <div
                ref={sheetRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={dialogTitleId}
                tabIndex={-1}
                className={sheetCls}
              >
                <div className={dragHandleCls} aria-hidden="true" />
                <div className={sheetHeaderCls}>
                  <span id={dialogTitleId} className={sheetNameCls}>
                    {fullName}
                  </span>
                  {email !== '' && (
                    <span className={sheetMetaCls} title={email}>
                      {email}
                    </span>
                  )}
                </div>
                <span className={sheetSectionCls}>Account</span>
                {/* Profile — Drop-1 decorative no-op (closes the sheet). */}
                <button type="button" className={sheetItemCls} onClick={runAndClose(() => {})}>
                  <span className={sheetItemIconCls}>
                    <Icon as={icons.User} size={16} />
                  </span>
                  Profile
                </button>
                <button type="button" className={sheetItemCls} onClick={runAndClose(onSignOut)}>
                  <span className={sheetItemIconCls}>
                    <Icon as={icons.CornerDownLeft} size={16} />
                  </span>
                  Sign Out
                </button>
              </div>
            </div>
          </>,
          document.body,
        )}
    </>
  );
}
CompactUserMenu.displayName = 'CompactUserMenu';
